/**
 * HTTP client for the FastAPI backend.
 *
 * Responsibilities:
 *  - Send credentials (cookie-based session) on every request.
 *  - Attach `X-CSRF-Token` header on state-changing requests using a token
 *    held in module-local memory (NOT in localStorage — XSS-safer).
 *  - Centralise error handling: 401 → bubble a typed `UnauthorizedError`
 *    that the router/QueryClient can react to (redirect to /login).
 *
 * The token is fetched once per session via `GET /api/auth/csrf` after login;
 * `setCsrfToken()` stores it. On logout, 401 responses, and explicit
 * `csrf_invalid` responses we clear it via `clearCsrfToken()`.
 */

const BASE = ""; // same-origin via Vite proxy in dev / same host in prod

let _csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  _csrfToken = token;
}

export function getCsrfToken(): string | null {
  return _csrfToken;
}

export function clearCsrfToken(): void {
  _csrfToken = null;
}

export class HttpError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.detail = detail;
  }
}

export class UnauthorizedError extends HttpError {
  constructor(detail: unknown) {
    super(401, detail, "unauthorized");
    this.name = "UnauthorizedError";
  }
}

function isCsrfInvalidPayload(payload: unknown): boolean {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "detail" in payload &&
    (payload as { detail?: unknown }).detail === "csrf_invalid"
  );
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, headers = {}, signal } = options;
  const isStateChanging = method !== "GET" && method !== "HEAD" && method !== "OPTIONS";

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };
  if (body !== undefined) {
    finalHeaders["Content-Type"] = "application/json";
  }
  if (isStateChanging && _csrfToken) {
    finalHeaders["X-CSRF-Token"] = _csrfToken;
  }

  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    payload = await response.json().catch(() => null);
  } else {
    payload = await response.text().catch(() => null);
  }

  if (!response.ok) {
    if (response.status === 403 && isCsrfInvalidPayload(payload)) {
      clearCsrfToken();
    }
    if (response.status === 401) {
      clearCsrfToken();
      throw new UnauthorizedError(payload);
    }
    throw new HttpError(response.status, payload, `${response.status} ${response.statusText}`);
  }

  return payload as T;
}

export const api = {
  get: <T = unknown>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "GET" }),
  post: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    apiRequest<T>(path, { ...opts, method: "POST", body }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    apiRequest<T>(path, { ...opts, method: "PATCH", body }),
  put: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    apiRequest<T>(path, { ...opts, method: "PUT", body }),
  delete: <T = unknown>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "DELETE" }),
};
